import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import isEqual from 'lodash/isEqual';

import { TABNAME_MAX_LENGTH } from '@/features/Dashboard/constants/const';
import { normalizeTabEntry } from '@/features/Dashboard/lib/transformers';
import { getUniqueTabName } from '@/features/Dashboard/utils/getUniqueTabName';
import { createNameSchema } from '@/features/Dashboard/utils/validateName';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';

export type DraftTab = {
  id: string;
  name: string;
  sourceName: string | null; // Original name from server (null for new tabs)
};

type UseDashboardTabsParams = {
  tabNames: string[];
  currentTabId: string | null;
  setCurrentTabId: Dispatch<SetStateAction<string | null>>;
  activeTab: number;
  currentDashboardTabs?: Record<string, any> | null;
  onTabChange?: (index: number, tabName?: string) => void;
};

export type UseDashboardTabsReturn = {
  draftTabs: DraftTab[];
  draftTabNames: string[];
  tabSourceNames: (string | null)[];
  setHoveredTabIndex: Dispatch<SetStateAction<number | null>>;
  handleRenameTab: (tabId: string, nextName: string) => void;
  handleAddTabDraft: () => void;
  handleAddTabWithId: (tabName: string, tabId: string) => void;
  handleDeleteTab: (tabId: string) => void;
  handleReorderTabs: (sourceIndex: number, targetIndex: number) => void;
  handleDraftTabChange: (draftIndex: number) => void;
  getDraftTabNameError: (value: string, tabId: string) => string | undefined;
  hasDuplicateTabNames: boolean;
  draftActiveTabIndex: number;
  hasHeaderTabNameErrors: boolean;
  hasTabOrderChanges: boolean;
  hasNewTabs: boolean;
  resetDraftTabs: (nextTabNames: string[]) => void;
  getCurrentTab: () => DraftTab | undefined;
};

const reorderList = <T>(list: T[], sourceIndex: number, targetIndex: number): T[] => {
  const next = [...list];
  const [moved] = next.splice(sourceIndex, 1);
  next.splice(targetIndex, 0, moved);
  return next;
};

export const useDashboardTabs = ({
  tabNames,
  currentTabId,
  setCurrentTabId,
  activeTab,
  currentDashboardTabs,
  onTabChange
}: UseDashboardTabsParams): UseDashboardTabsReturn => {
  // Initialize draftTabs with IDs from existing tabs or generate new ones
  const initializeDraftTabs = useCallback(
    (names: string[]): DraftTab[] => {
      return names.map(name => {
        // Try to find existing tab and preserve its ID
        if (currentDashboardTabs?.[name]) {
          const tabValue = currentDashboardTabs[name];
          const normalized = normalizeTabEntry(name, tabValue);
          return {
            id: normalized.id,
            name,
            sourceName: name // Original name matches current name initially
          };
        }
        // New tab - generate new ID
        return {
          id: generateUniqueUUID(new Set()),
          name,
          sourceName: null
        };
      });
    },
    [currentDashboardTabs]
  );

  const [draftTabs, setDraftTabs] = useState<DraftTab[]>(() => initializeDraftTabs(tabNames));
  const [hoveredTabIndex, setHoveredTabIndex] = useState<number | null>(null);

  // Update draftTabs when tabNames change (e.g., from props)
  useEffect(() => {
    setDraftTabs(prev => {
      // Only update if the names actually changed
      const currentNames = prev.map(t => t.name);
      if (isEqual(currentNames, tabNames)) {
        return prev;
      }
      return initializeDraftTabs(tabNames);
    });
  }, [tabNames, initializeDraftTabs]);

  const tabNameSchema = useMemo(() => createNameSchema('Tab name', TABNAME_MAX_LENGTH), []);

  const getDraftTabNameError = useCallback(
    (value: string, tabId: string): string | undefined => {
      const parsed = tabNameSchema.safeParse(value ?? '');
      if (!parsed.success) {
        return parsed.error.issues[0]?.message || 'Invalid tab name';
      }
      const duplicateTab = draftTabs.find(
        tab => tab.id !== tabId && tab.name.trim() === value.trim()
      );
      if (duplicateTab) {
        return 'Tab name must be unique';
      }
      return undefined;
    },
    [draftTabs, tabNameSchema]
  );

  const handleAddTabDraft = useCallback(() => {
    const existingNames = draftTabs.map(t => t.name);
    const newTabName = getUniqueTabName(existingNames);
    const newTab: DraftTab = {
      id: generateUniqueUUID(new Set(draftTabs.map(t => t.id))),
      name: newTabName,
      sourceName: null // New tab
    };
    setDraftTabs(prev => [...prev, newTab]);
  }, [draftTabs]);

  const handleAddTabWithId = useCallback(
    (tabName: string, tabId: string) => {
      // Check if tab already exists
      const existingTab = draftTabs.find(tab => tab.id === tabId || tab.name === tabName);
      if (existingTab) {
        return; // Tab already exists
      }
      const newTab: DraftTab = {
        id: tabId,
        name: tabName,
        sourceName: null // New tab
      };
      setDraftTabs(prev => [...prev, newTab]);
    },
    [draftTabs]
  );

  const handleDeleteTab = useCallback(
    (tabId: string) => {
      // Prevent deleting the last tab
      if (draftTabs.length <= 1) {
        return;
      }

      setDraftTabs(prev => {
        const tabIndex = prev.findIndex(tab => tab.id === tabId);
        if (tabIndex === -1) return prev;

        const nextTabs = prev.filter(tab => tab.id !== tabId);

        // If the deleted tab was the current one, switch to another tab
        if (currentTabId === tabId) {
          // Switch to the previous tab if available, otherwise the first tab
          const newIndex = tabIndex > 0 ? tabIndex - 1 : 0;
          const newTab = nextTabs[newIndex];
          if (newTab) {
            setCurrentTabId(newTab.id);
            // Call onTabChange if provided
            if (onTabChange) {
              onTabChange(newIndex, newTab.name);
            }
          }
        }

        return nextTabs;
      });

      // Reset hover state if the deleted tab was hovered
      setHoveredTabIndex(null);
    },
    [draftTabs, currentTabId, setCurrentTabId, onTabChange]
  );

  const handleRenameTab = useCallback(
    (tabId: string, nextName: string) => {
      setDraftTabs(prev => {
        const next = prev.map(tab => {
          if (tab.id === tabId) {
            // Preserve ID and sourceName when renaming
            return {
              ...tab,
              name: nextName
            };
          }
          return tab;
        });
        return next;
      });

      // Update currentTabId if the renamed tab is the current one
      if (currentTabId === tabId) {
        // Keep the same ID, just update the name reference if needed
        // The ID stays the same, which is what we want
      }
    },
    [currentTabId]
  );

  const handleReorderTabs = useCallback((sourceIndex: number, targetIndex: number) => {
    if (sourceIndex === targetIndex) return;
    setDraftTabs(prev => reorderList(prev, sourceIndex, targetIndex));
    setHoveredTabIndex(prev => {
      if (prev === null) return prev;
      if (prev === sourceIndex) return targetIndex;
      return prev;
    });
  }, []);

  const handleDraftTabChange = useCallback(
    (draftIndex: number) => {
      const tab = draftTabs[draftIndex];
      if (!tab) return;

      // Update the current tab ID
      setCurrentTabId(tab.id);

      // Call onTabChange if provided
      if (onTabChange) {
        onTabChange(draftIndex, tab.name);
      }
    },
    [draftTabs, onTabChange, setCurrentTabId]
  );

  const draftTabNames = useMemo(() => draftTabs.map(t => t.name), [draftTabs]);
  const tabSourceNames = useMemo(() => draftTabs.map(t => t.sourceName), [draftTabs]);

  const draftActiveTabIndex = useMemo(() => {
    if (!currentTabId) return activeTab;
    const index = draftTabs.findIndex(tab => tab.id === currentTabId);
    return index === -1 ? activeTab : index;
  }, [activeTab, currentTabId, draftTabs]);

  const hasHeaderTabNameErrors = useMemo(() => {
    return draftTabs.some(tab => !!getDraftTabNameError(tab.name, tab.id));
  }, [draftTabs, getDraftTabNameError]);

  const hasDuplicateTabNames = useMemo(() => {
    const trimmedNames = draftTabs.map(tab => tab.name.trim().toLowerCase());
    const uniqueNames = new Set(trimmedNames);
    return trimmedNames.length !== uniqueNames.size;
  }, [draftTabs]);

  const hasTabOrderChanges = useMemo(() => {
    return !isEqual(draftTabNames, tabNames);
  }, [draftTabNames, tabNames]);

  const hasNewTabs = useMemo(() => {
    return draftTabs.some(tab => tab.sourceName === null);
  }, [draftTabs]);

  const resetDraftTabs = useCallback(
    (nextTabNames: string[]) => {
      setDraftTabs(initializeDraftTabs(nextTabNames));
      setHoveredTabIndex(null);
    },
    [initializeDraftTabs]
  );

  const getCurrentTab = useCallback((): DraftTab | undefined => {
    if (!currentTabId) return undefined;
    return draftTabs.find(tab => tab.id === currentTabId);
  }, [currentTabId, draftTabs]);

  return {
    draftTabs,
    draftTabNames,
    tabSourceNames,
    setHoveredTabIndex,
    handleRenameTab,
    handleAddTabDraft,
    handleAddTabWithId,
    handleDeleteTab,
    handleReorderTabs,
    handleDraftTabChange,
    getDraftTabNameError,
    hasDuplicateTabNames,
    draftActiveTabIndex,
    hasHeaderTabNameErrors,
    hasTabOrderChanges,
    hasNewTabs,
    resetDraftTabs,
    getCurrentTab
  };
};
